import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useFadeIn } from "@/hooks/useFadeIn";
import { useLanguage } from "@/i18n/LanguageContext";
import { translations } from "@/i18n/translations";
import { MapPin, Building2, TrendingUp, Waves, Trees, Sun, ChevronLeft, ChevronRight } from "lucide-react";
import { useProperties } from "@/hooks/useProperties";

import gardenImage1 from "@/assets/optimized/home-habitat-garden-1.jpg";
import gardenImage2 from "@/assets/optimized/home-habitat-garden-2.jpg";
import gardenImage3 from "@/assets/optimized/home-habitat-garden-3.jpg";
import towerImage1 from "@/assets/optimized/home-habitat-tower-1.jpg";
import towerImage2 from "@/assets/optimized/home-habitat-tower-2.jpg";
import towerImage3 from "@/assets/optimized/home-habitat-tower-3.jpg";

const gardenImages = [gardenImage1, gardenImage2, gardenImage3];
const towerImages = [towerImage1, towerImage2, towerImage3];

interface ImageSliderProps {
  images: string[];
  alt: string;
}

const ImageSlider = ({ images, alt }: ImageSliderProps) => {
  const [current, setCurrent] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const next = useCallback(() => setCurrent((i) => (i + 1) % images.length), [images.length]);
  const prev = useCallback(() => setCurrent((i) => (i - 1 + images.length) % images.length), [images.length]);

  useEffect(() => {
    if (isHovered) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(next, 4000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isHovered, next]);

  const visibleIndices = useMemo(() => {
    const indices = new Set<number>();
    for (let offset = -1; offset <= 1; offset++) {
      indices.add((current + offset + images.length) % images.length);
    }
    return indices;
  }, [current, images.length]);

  const maxDots = 9;
  const showCompactDots = images.length > maxDots;

  return (
    <div
      className="group aspect-[4/3] relative overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className="flex h-full will-change-transform transition-transform duration-500 ease-out"
        style={{ transform: `translate3d(-${current * 100}%, 0, 0)` }}
      >
        {images.map((src, i) => (
          <div key={i} className="w-full h-full shrink-0">
            {visibleIndices.has(i) ? (
              <img
                src={src}
                  alt={`${alt} - ${i + 1}`}
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  width={1400}
                  height={1050}
                />
            ) : (
              <div className="w-full h-full bg-muted" />
            )}
          </div>
        ))}
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-black/18 to-transparent" />

      <button
        onClick={(e) => { e.stopPropagation(); prev(); }}
        className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-background/70 backdrop-blur-sm opacity-0 transition-opacity duration-300 hover:bg-background/90 group-hover:opacity-100"
        aria-label="Previous image"
      >
        <ChevronLeft className="w-4 h-4 text-foreground" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); next(); }}
        className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-background/70 backdrop-blur-sm opacity-0 transition-opacity duration-300 hover:bg-background/90 group-hover:opacity-100"
        aria-label="Next image"
      >
        <ChevronRight className="w-4 h-4 text-foreground" />
      </button>

      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 items-center">
        {showCompactDots ? (
          <span className="text-[10px] text-background/80 font-body tabular-nums">
            {current + 1} / {images.length}
          </span>
        ) : (
          images.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
              className={`flex h-8 w-8 items-center justify-center rounded-full transition-all duration-300 ${
                i === current ? "text-accent" : "text-background/60"
              }`}
              aria-label={`Go to image ${i + 1}`}
            >
              <span
                className={`block h-1.5 rounded-full transition-all duration-300 ${
                  i === current ? "w-4 bg-accent" : "w-1.5 bg-current"
                }`}
              />
            </button>
          ))
        )}
      </div>
    </div>
  );
};

const FeaturedProperty = () => {
  const { ref, visible } = useFadeIn();
  const { t } = useLanguage();

  // Phase 5.1: Optionally pull homepage properties from Supabase.
  // Prefer region=DR; fall back to any homepage property; then is_featured.
  const { data: homepageDR } = useProperties({ homepageOnly: true, region: "DR", withImages: true });
  const { data: homepageAny } = useProperties({ homepageOnly: true, withImages: true });
  const { data: featuredAny } = useProperties({ featuredOnly: true, withImages: true });
  const dbFeatured = (homepageDR.length > 0
    ? homepageDR
    : homepageAny.length > 0
      ? homepageAny
      : featuredAny
  ).slice(0, 2);

  const fallbackProperties = [
    {
      name: "Habitat Beach Garden",
      location: "Juan Dolio, Dominican Republic",
      description: t(translations.featured.garden.description),
      type: "Investment Opportunity",
      price: "Contact for pricing",
      images: gardenImages,
      href: "/dominican-republic-investing/properties/habitat-beach-garden-juan-dolio",
      highlights: [
        { icon: Waves, text: t(translations.featured.garden.h1) },
        { icon: Trees, text: t(translations.featured.garden.h2) },
        { icon: TrendingUp, text: t(translations.featured.garden.h3) },
      ],
    },
    {
      name: "Habitat Beach Tower",
      location: "Juan Dolio, Dominican Republic",
      description: t(translations.featured.tower.description),
      type: "Luxury Residence",
      price: "Contact for pricing",
      images: towerImages,
      href: "/dominican-republic-investing/properties/habitat-beach-tower-juan-dolio",
      highlights: [
        { icon: Sun, text: t(translations.featured.tower.h1) },
        { icon: Building2, text: t(translations.featured.tower.h2) },
        { icon: MapPin, text: t(translations.featured.tower.h3) },
      ],
    },
  ];

  const properties = fallbackProperties.map((fb, idx) => {
    const db = dbFeatured[idx];
    if (!db) return fb;
    const dbImages = [
      ...(db.hero_image_url ? [db.hero_image_url] : []),
      ...db.images.map((i) => i.image_url),
    ];
    const href =
      (db.cta_url && db.cta_url.trim()) ||
      (db.region === "DR"
        ? `/dominican-republic-investing/properties/${db.slug}`
        : "/listings");
    return {
      name: db.title || fb.name,
      location: db.location || fb.location,
      description: db.short_description || db.description || fb.description,
      type: db.listing_status || fb.type,
      price: db.price || fb.price,
      images: dbImages.length > 0 ? dbImages : fb.images,
      href,
      highlights: fb.highlights,
    };
  });

  return (
    <section id="properties" className="mt-8 bg-background py-10 md:mt-10 md:py-14">
      <div
        ref={ref}
        className={`mx-auto max-w-wide px-5 md:px-12 transition-all duration-700 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="mb-10 text-center md:mb-12">
          <span className="font-body text-[11px] uppercase tracking-[0.3em] text-accent mb-4 block">
            Global Opportunity
          </span>
          <div className="mx-auto mb-4 h-px w-20 bg-accent/65" />
          <h2 className="font-headline text-4xl md:text-5xl lg:text-[4rem] text-foreground mb-4 leading-[1.06]">
            Featured Dominican Republic Opportunities
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            Explore select investment properties in the Dominican Republic, with personalized Massachusetts property guidance available by consultation.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-10">
          {properties.map((property, idx) => (
            <div
              key={property.name}
              className="group overflow-hidden border border-border/70 bg-card transition-all duration-500 hover:-translate-y-0.5 hover:shadow-[0_16px_44px_-16px_rgba(0,0,0,0.14)]"
            >
              {property.images.length > 1 ? (
                <ImageSlider images={property.images} alt={`${property.name} development`} />
              ) : (
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={property.images[0]}
                    alt={`${property.name} development in ${property.location}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                    width={1400}
                    height={1050}
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-black/18 to-transparent" />
                </div>
              )}

              <div className="p-7 md:p-10">
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <span className="border border-accent/35 bg-accent/8 px-3 py-1 font-body text-[10px] uppercase tracking-[0.18em] text-accent">
                    {property.type}
                  </span>
                  <span className="font-body text-base font-semibold text-foreground">
                    {property.price}
                  </span>
                </div>
                <h3 className="mb-2 font-headline text-[2rem] leading-tight text-foreground md:text-[2.45rem]">
                  {property.name}
                </h3>
                <p className="mb-5 font-body text-sm uppercase tracking-[0.18em] text-accent/90">
                  {property.location}
                </p>
                <p className="text-muted-foreground text-base leading-relaxed mb-7">
                  {property.description}
                </p>

                <div className="flex flex-col gap-3 mb-9">
                  {property.highlights.map(({ icon: Icon, text }) => (
                    <div key={text} className="flex items-center gap-3 text-base text-foreground">
                      <Icon className="w-5 h-5 text-accent shrink-0" strokeWidth={1.5} />
                      <span>{text}</span>
                    </div>
                  ))}
                </div>

                <Button variant="outline" size="default" className="w-full border-accent/25 text-foreground sm:w-auto transition-transform duration-300 hover:-translate-y-px hover:border-accent/50" asChild>
                  <Link to={property.href}>View Details</Link>
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Button variant="outline" size="lg" className="border-accent/30 text-foreground hover:border-accent/60 transition-transform duration-300 hover:-translate-y-px" asChild>
            <Link to="/dominican-republic-investing/properties">View All DR Listings</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProperty;
